import React from 'react';
import type { ExternalApiKey } from '../types';
import { Avatar, AvatarFallback } from '@/shared/components/ui/avatar';
import { CardActionsMenu } from '@/shared/components/ui/CardActionsMenu';
import type { CardAction } from '@/shared/components/ui/CardActionsMenu';
import { StatusPill } from '@/shared/components/ui';
import { Icon } from '@/shared/icons';

/**
 * Linha de tabela de API Key (view "Lista"). Padrão-lista travado:
 * checkbox de seleção (1ª col) + coluna multi-linha (nome+descrição) +
 * menu ••• (última col). Avatar sem foto = slate #64748b com ícone de chave.
 *
 * Não há edição de API Key (segurança: chave completa nunca reexibida).
 */
export interface ApiKeyListItemProps {
  apiKey: ExternalApiKey;
  selected?: boolean;
  onToggleSelect?: (id: string) => void;
  onDelete?: (id: string) => void;
  onToggleStatus?: (id: string, currentStatus: string) => void;
}

export const ApiKeyListItem: React.FC<ApiKeyListItemProps> = ({
  apiKey,
  selected = false,
  onToggleSelect,
  onDelete,
  onToggleStatus,
}) => {
  const formatDate = (dateString?: string) => {
    if (!dateString) return 'Nunca';
    return new Date(dateString).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  };

  const isActive = apiKey.status === 'active';

  const actions: CardAction[] = [];
  if (onToggleStatus) {
    actions.push({
      label: isActive ? 'Desativar' : 'Ativar',
      icon: 'power',
      onClick: () => onToggleStatus(apiKey.id, apiKey.status),
    });
  }
  if (onDelete) {
    actions.push({ label: 'Excluir', icon: 'trash', onClick: () => onDelete(apiKey.id), variant: 'danger' });
  }

  return (
    <tr
      className="border-b transition-colors"
      style={{ borderColor: 'var(--theme-card-border)', backgroundColor: selected ? 'var(--theme-primary-background)' : undefined }}
      onMouseEnter={(e) => { if (!selected) e.currentTarget.style.backgroundColor = 'var(--theme-button-ghost-hover)'; }}
      onMouseLeave={(e) => { if (!selected) e.currentTarget.style.backgroundColor = 'transparent'; }}
      data-testid={`apikey-row-${apiKey.id}`}
    >
      {/* Checkbox */}
      <td className="pl-4 pr-1 py-3 w-11">
        <button
          type="button"
          onClick={() => onToggleSelect?.(apiKey.id)}
          className="w-[18px] h-[18px] rounded-[5px] border-[1.6px] flex items-center justify-center transition-colors"
          style={{
            backgroundColor: selected ? 'var(--theme-primary)' : 'transparent',
            borderColor: selected ? 'var(--theme-primary)' : 'var(--theme-input-border)',
            color: '#ffffff',
          }}
          aria-label={selected ? 'Desmarcar' : 'Selecionar'}
          data-testid={`apikey-check-${apiKey.id}`}
        >
          {selected && <Icon name="check" className="w-3 h-3" />}
        </button>
      </td>

      {/* Chave: avatar + nome + descrição */}
      <td className="px-4 py-3">
        <div className="flex items-center gap-3 min-w-0">
          <Avatar className="w-9 h-9 flex-shrink-0" isSquircle>
            <AvatarFallback isSquircle>
              <Icon name="key" className="w-4 h-4 text-white" />
            </AvatarFallback>
          </Avatar>
          <div className="min-w-0">
            <div className="font-semibold text-sm truncate" style={{ color: 'var(--theme-card-text)' }}>
              {apiKey.name}
            </div>
            {apiKey.description && (
              <div className="text-xs truncate" style={{ color: 'var(--theme-muted-text)' }}>
                {apiKey.description}
              </div>
            )}
          </div>
        </div>
      </td>

      {/* Chave mascarada */}
      <td className="px-4 py-3">
        <code
          className="text-xs px-2 py-1 rounded font-mono whitespace-nowrap"
          style={{
            backgroundColor: 'var(--theme-surface-sunken)',
            border: '1px solid var(--theme-card-border)',
            color: 'var(--theme-card-text)',
          }}
        >
          {apiKey.maskedKey}
        </code>
      </td>

      {/* Status (F47/F62-65: StatusPill shared) */}
      <td className="px-4 py-3">
        <StatusPill
          variant={isActive ? 'success' : 'neutral'}
          label={isActive ? 'Ativa' : 'Inativa'}
          data-testid={`apikey-status-${apiKey.id}`}
        />
      </td>

      {/* Uso */}
      <td className="px-4 py-3 text-sm tabular-nums" style={{ color: 'var(--theme-card-text)' }}>
        {apiKey.usageCount}
      </td>

      {/* Último uso */}
      <td className="px-4 py-3 text-sm whitespace-nowrap" style={{ color: 'var(--theme-muted-text)' }}>
        {formatDate(apiKey.lastUsedAt)}
      </td>

      {/* Expira em */}
      <td className="px-4 py-3 text-sm whitespace-nowrap" style={{ color: apiKey.expiresAt ? 'var(--theme-warning-color)' : 'var(--theme-muted-text)' }}>
        {apiKey.expiresAt ? formatDate(apiKey.expiresAt) : 'Nunca'}
      </td>

      {/* Ações */}
      <td className="px-4 py-3 text-right w-14">
        {actions.length > 0 && <CardActionsMenu actions={actions} size="sm" />}
      </td>
    </tr>
  );
};

ApiKeyListItem.displayName = 'ApiKeyListItem';
